import { LineItemElementProps } from "../types";

/**
 * After elements are added or deleted the numeric elementIndex values stored inside
 * valueDependencies can become stale (they reference old array positions). This function
 * builds an old-index → new-index map by matching variable elements on their value text,
 * then rewrites every elementIndex reference across the new elements array.
 * Dependencies that pointed to a variable that no longer exists are dropped.
 */
export const recalculateElementIndexes = (
	oldElements: LineItemElementProps[],
	newElements: LineItemElementProps[]
): LineItemElementProps[] => {
	// Map old array position → new array position for every variable element
	const indexMap = new Map<number, number>();
	oldElements.forEach((oldEl, oldIdx) => {
		if (oldEl.type !== "variable") return;
		const newIdx = newElements.findIndex(
			(newEl) => newEl.type === "variable" && newEl.value.trim() === oldEl.value.trim()
		);
		if (newIdx !== -1) {
			indexMap.set(oldIdx, newIdx);
		}
	});

	return newElements.map((element) => {
		if (!element.valueDependencies) return element;

		const remappedDeps: typeof element.valueDependencies = {};
		for (const [value, deps] of Object.entries(element.valueDependencies)) {
			const updated = deps
				.map((dep) => {
					const newIdx = indexMap.get(dep.elementIndex);
					if (newIdx === undefined) return null; // referenced element was removed
					return { ...dep, elementIndex: newIdx };
				})
				.filter((dep): dep is NonNullable<typeof dep> => dep !== null);

			if (updated.length > 0) {
				remappedDeps[value] = updated;
			}
		}

		if (Object.keys(remappedDeps).length === 0) {
			const { valueDependencies, ...rest } = element;
			return rest;
		}
		return { ...element, valueDependencies: remappedDeps };
	});
};
